import React, { useEffect, useRef, useState } from "react";
import { FaTimes } from "react-icons/fa";

const IMAGE_EXTENSIONS = new Set([
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "bmp",
  "svg",
  "avif",
]);

function getFileExtension(file) {
  const name = String(file?.name || file?.file_name || file?.filename || "");
  const match = name.match(/\.([^.]+)$/i);
  return match ? match[1].toLowerCase() : "";
}

function getPreviewType(file, blob) {
  const mimeType = String(blob?.type || file?.mime_type || file?.content_type || "")
    .toLowerCase()
    .split(";")[0]
    .trim();
  const extension = getFileExtension(file);

  if (mimeType === "application/pdf" || extension === "pdf") return "pdf";
  if (mimeType.startsWith("image/") || IMAGE_EXTENSIONS.has(extension)) return "image";
  return "unsupported";
}

/**
 * Fetches a protected document through the caller's API handler and displays
 * image/PDF content in the application without changing the download flow.
 */
export default function DocumentPreviewModal({
  isOpen,
  file,
  loadPreview,
  onClose,
  title = "Document preview",
}) {
  const loadPreviewRef = useRef(loadPreview);
  const [preview, setPreview] = useState(null);

  loadPreviewRef.current = loadPreview;

  const fileKey = file
    ? String(file.id ?? file.file_id ?? file.submission_id ?? file.name ?? file.file_name ?? "")
    : "";

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;

    if (!isOpen || !file) {
      setPreview(null);
      return undefined;
    }

    setPreview({ loading: true, url: "", type: "", error: "" });

    (async () => {
      try {
        const blob = await loadPreviewRef.current(file);
        if (!(blob instanceof Blob)) {
          throw new Error("The server did not return a previewable file.");
        }

        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) {
          setPreview({
            loading: false,
            url: objectUrl,
            type: getPreviewType(file, blob),
            error: "",
          });
        }
      } catch (error) {
        if (!cancelled) {
          setPreview({
            loading: false,
            url: "",
            type: "",
            error: error?.message || "Unable to preview this document.",
          });
        }
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file, fileKey, isOpen]);

  if (!isOpen || !file) return null;

  return (
    <div
      className="fixed inset-0 z-[12000] flex items-center justify-center bg-black/75 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="relative flex h-full max-h-[95vh] w-full max-w-6xl flex-col rounded-lg bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="flex items-center justify-between gap-4 border-b border-gray-200 px-4 py-3">
          <h2 className="min-w-0 truncate text-sm font-semibold text-gray-800">
            {file.name || file.file_name || file.filename || title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            aria-label="Close preview"
          >
            <FaTimes className="h-4 w-4" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-gray-100 p-4">
          {!preview || preview.loading ? (
            <div className="rounded-lg bg-white px-8 py-10 text-sm text-gray-600 shadow-sm">
              Loading preview...
            </div>
          ) : preview?.error ? (
            <div className="rounded-lg bg-white px-8 py-10 text-center text-sm text-red-600 shadow-sm">
              {preview.error}
            </div>
          ) : preview?.type === "image" ? (
            <img
              src={preview.url}
              alt={file.name || file.file_name || "Document preview"}
              className="max-h-full max-w-full rounded bg-white object-contain shadow-sm"
            />
          ) : preview?.type === "pdf" ? (
            <iframe
              src={preview.url}
              title={file.name || file.file_name || "PDF preview"}
              className="h-full min-h-[70vh] w-full rounded border-0 bg-white"
            />
          ) : (
            <div className="rounded-lg bg-white px-8 py-10 text-center text-sm text-gray-600 shadow-sm">
              This file type cannot be previewed in the application.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
